import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import {
  Inbox, Clock, CheckCircle2, XCircle, DollarSign, TrendingUp, RefreshCw,
  Users, Bell, ArrowUpRight, Sparkles,
} from "lucide-react";
import {
  LineChart, Line, Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const NEXON = { indigo: "#636AC3", ink: "#1E1E3F", mint: "#C9E9A4", cloud: "#FAF9FE", ash: "#7A7A99" };

const STAT_CARDS = [
  { key: "submitted",    label: "Submitted",     Icon: Inbox,         tone: { bg: "#E7E8F6", ring: "#C7C9E7", fg: NEXON.indigo } },
  { key: "under_review", label: "Under review",  Icon: Clock,         tone: { bg: "#FFF4DA", ring: "#F4D48A", fg: "#8B6A11" } },
  { key: "approved",     label: "Approved",      Icon: CheckCircle2,  tone: { bg: "#E9F7E1", ring: "#C9E9A4", fg: "#3E7A1E" } },
  { key: "rejected",     label: "Rejected",      Icon: XCircle,       tone: { bg: "#FDECEF", ring: "#F7B5C0", fg: "#B22C40" } },
  { key: "paid",         label: "Paid",          Icon: DollarSign,    tone: { bg: "#E7F5EF", ring: "#B5DDC7", fg: "#137A57" } },
];

const STATUS_COLORS = {
  submitted:    "#636AC3",
  under_review: "#F59E0B",
  approved:     "#10B981",
  paid:         "#137A57",
  rejected:     "#EF4444",
  duplicate:    "#94A3B8",
};

function buildTrend(rows) {
  const map = {};
  for (let i = 29; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000);
    const key = d.toISOString().slice(0, 10);
    map[key] = { d: key.slice(5), count: 0, key };
  }
  for (const r of rows || []) {
    const key = (r.created_at || "").slice(0, 10);
    if (map[key]) map[key].count += 1;
  }
  return Object.values(map);
}

const fmtRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)     return `${Math.floor(diff)}s ago`;
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

function StatCard({ label, value, Icon, tone, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 lg:p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center border" style={{ backgroundColor: tone.bg, borderColor: tone.ring, color: tone.fg }}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{label}</span>
      </div>
      <div className="mt-3 text-3xl font-black tabular-nums" style={{ color: NEXON.ink }}>{value}</div>
    </div>
  );
}

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/overview");
      setData(r.data);
    } catch { toast.error("Couldn't load overview"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const trend = useMemo(() => buildTrend(data?.trend || data?.recent || []), [data]);
  const trendTotal = useMemo(() => trend.reduce((s, r) => s + r.count, 0), [trend]);
  const trendPeak  = useMemo(() => Math.max(0, ...trend.map((r) => r.count)), [trend]);

  const counts = data?.counts || {};
  const revenue = data?.revenue_paid ?? 0;
  const recent = (data?.recent || data?.recent_submissions || []).slice(0, 10);

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 lg:p-8" style={{ backgroundColor: NEXON.cloud }} data-testid="admin-overview">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-[10px] font-black tracking-widest uppercase" style={{ color: NEXON.indigo }}>Overview</span>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight mt-1" style={{ color: NEXON.ink }}>
              How GC Nexon is doing today
            </h1>
            <p className="text-sm mt-1" style={{ color: NEXON.ash }}>
              Live snapshot — submission funnel, revenue paid, and the last 30 days of activity.
            </p>
            <div className="mt-3 h-[3px] w-24 rounded-full" style={{ backgroundImage: `linear-gradient(90deg, ${NEXON.indigo}, ${NEXON.ink}, ${NEXON.mint})` }} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/admin/submissions"
              className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl bg-white border border-slate-200 hover:border-indigo-300 text-sm font-black text-slate-700"
              data-testid="overview-view-submissions"
            >
              <ArrowUpRight size={14} /> View submissions
            </Link>
            <button
              onClick={load}
              disabled={loading}
              data-testid="overview-refresh"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-xl text-white text-sm font-black shadow-sm disabled:opacity-60"
              style={{ backgroundColor: NEXON.indigo }}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Primary stat grid */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-6 gap-3">
          {STAT_CARDS.map((c) => (
            <StatCard
              key={c.key}
              label={c.label}
              value={counts[c.key] ?? 0}
              Icon={c.Icon}
              tone={c.tone}
              testId={`stat-${c.key}`}
            />
          ))}
          <div className="col-span-2 lg:col-span-1 rounded-2xl p-4 lg:p-5 text-white" style={{ background: `linear-gradient(135deg, ${NEXON.indigo}, ${NEXON.ink})` }} data-testid="stat-revenue">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(255,255,255,0.15)" }}>
                <TrendingUp className="w-5 h-5" />
              </div>
              <span className="text-[9px] font-black uppercase tracking-widest opacity-70">Revenue paid</span>
            </div>
            <div className="mt-3 text-3xl font-black tabular-nums">
              ${Number(revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {/* Secondary metric row */}
        <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: NEXON.mint, color: NEXON.ink }}><Users size={16} /></div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Customers</div>
              <div className="text-xl font-black tabular-nums" style={{ color: NEXON.ink }}>{data?.customers ?? 0}</div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-rose-100 text-rose-700"><Bell size={16} /></div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Open tickets</div>
              <div className="text-xl font-black tabular-nums" style={{ color: NEXON.ink }}>{data?.open_tickets ?? 0}</div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-700"><Sparkles size={16} /></div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">30-day submissions</div>
              <div className="text-xl font-black tabular-nums" style={{ color: NEXON.ink }}>{trendTotal}</div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-100 text-amber-700"><TrendingUp size={16} /></div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Peak day</div>
              <div className="text-xl font-black tabular-nums" style={{ color: NEXON.ink }}>{trendPeak}</div>
            </div>
          </div>
        </div>

        {/* Trend chart */}
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 lg:p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-black" style={{ color: NEXON.ink }}>Daily submissions</h3>
              <p className="text-xs text-slate-500">Rolling 30 days · total {trendTotal}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-[10px] font-black uppercase tracking-widest text-indigo-700">
              <TrendingUp size={11} /> Peak {trendPeak}
            </span>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="nexonFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={NEXON.indigo} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={NEXON.indigo} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#F1F5F9" strokeDasharray="3 3" />
                <XAxis dataKey="d" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12, background: "white", boxShadow: "0 6px 18px -6px rgba(30,30,63,0.15)" }}
                  labelStyle={{ fontWeight: 800, color: NEXON.ink }}
                />
                <Area type="monotone" dataKey="count" stroke={NEXON.indigo} strokeWidth={2.5} fill="url(#nexonFill)" />
                <Line type="monotone" dataKey="count" stroke={NEXON.indigo} strokeWidth={2.5} dot={{ r: 3, fill: NEXON.indigo }} activeDot={{ r: 5 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent activity table */}
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-black" style={{ color: NEXON.ink }}>Recent activity</h3>
              <p className="text-xs text-slate-500">Latest 10 submissions across all statuses</p>
            </div>
            <Link to="/admin/submissions" className="text-xs font-black text-indigo-700 hover:text-indigo-900">See all →</Link>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 text-[10px] uppercase tracking-widest text-slate-500 font-black">
              <tr>
                <th className="text-left px-5 py-3">When</th>
                <th className="text-left px-5 py-3">Reference</th>
                <th className="text-left px-5 py-3">Brand</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3">Estimated</th>
              </tr>
            </thead>
            <tbody>
              {recent.length ? recent.map((r, i) => {
                const c = STATUS_COLORS[r.status] || "#64748B";
                return (
                  <tr key={r.id || i} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`overview-row-${i}`}>
                    <td className="px-5 py-3 text-slate-500 font-mono text-[11px]" title={new Date(r.created_at).toLocaleString()}>
                      {fmtRelative(r.created_at)}
                    </td>
                    <td className="px-5 py-3 font-mono text-[11px] font-black text-indigo-700">{r.reference || "—"}</td>
                    <td className="px-5 py-3 font-bold text-slate-800 truncate max-w-[180px]">{r.brand_name || r.brand || "—"}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border" style={{ color: c, borderColor: c + "33", backgroundColor: c + "12" }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c }} />
                        {(r.status || "").replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-mono font-black tabular-nums" style={{ color: NEXON.ink }}>
                      ${(r.estimated_payout ?? r.final_payout ?? 0).toFixed(2)}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={5} className="text-center py-14 text-slate-400 text-sm">No activity yet — new submissions will appear here.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
